import { getSafe } from '@/plugins/globals.plugin'
import * as asn1js from 'asn1js'
import { hexStringToBuffer } from '@/utils/text.util'

// KEY TYPES
const ALG_DES = 1
const ALG_3DES_112 = 2
const ALG_3DES_168 = 3
const ALG_RSA_512 = 4
const ALG_RSA_1024 = 5
const ALG_RSA_2048 = 6
const ALG_AES_128 = 7
const ALG_AES_192 = 8
const ALG_AES_256 = 9
const ALG_ARC4 = 10
const ALG_RSA_4096 = 11
const ALG_ECC_SECP112R1 = 18
const ALG_ECC_SECP112R2 = 19
const ALG_ECC_SECP128R1 = 20
const ALG_ECC_SECP128R2 = 21
const ALG_ECC_SECP160K1 = 22
const ALG_ECC_SECP160R1 = 23
const ALG_ECC_SECP160R2 = 24
const ALG_ECC_SECP192K1 = 25
const ALG_ECC_SECP192R1 = 26
const ALG_ECC_SECP224K1 = 27
const ALG_ECC_SECP224R1 = 28
const ALG_ECC_SECP256K1 = 29
const ALG_ECC_SECP256R1 = 30
const ALG_ECC_SECP384R1 = 31
const ALG_ECC_SECP521R1 = 32
const ALG_ECC_X9_62_PRIME192V2 = 33
const ALG_ECC_X9_62_PRIME192V3 = 34
const ALG_ECC_X9_62_PRIME239V1 = 35
const ALG_ECC_X9_62_PRIME239V2 = 36
const ALG_ECC_X9_62_PRIME239V3 = 37
const ALG_ECC_BRAINPOOL_P160R1 = 38
const ALG_ECC_BRAINPOOL_P160T1 = 39
const ALG_ECC_BRAINPOOL_P192R1 = 40
const ALG_ECC_BRAINPOOL_P192T1 = 41
const ALG_ECC_BRAINPOOL_P224R1 = 42
const ALG_ECC_BRAINPOOL_P224T1 = 43
const ALG_ECC_BRAINPOOL_P256R1 = 44
const ALG_ECC_BRAINPOOL_P256T1 = 45
const ALG_ECC_BRAINPOOL_P320R1 = 46
const ALG_ECC_BRAINPOOL_P320T1 = 47
const ALG_ECC_BRAINPOOL_P384R1 = 48
const ALG_ECC_BRAINPOOL_P384T1 = 49
const ALG_ECC_BRAINPOOL_P512R1 = 50
const ALG_ECC_BRAINPOOL_P512T1 = 51
const ALG_DESX = 91
const ALG_HMAC_MD5 = 92
const ALG_HMAC_SHA1 = 93
const ALG_HMAC_SHA2_256 = 94
const ALG_HMAC_SHA2_384 = 95
const ALG_HMAC_SHA2_512 = 96
const ALG_RSA_1152 = 121
const ALG_RSA_1408 = 122
const ALG_RSA_1984 = 123
const ALG_RSA_8192 = 124
const ALG_ECC_ED25519 = 201
const ALG_ECC_ED448 = 202
const ALG_ECC_X25519 = 203
const ALG_ECC_X448 = 204

// KEK
const KEK_WRAP_NULL_PAD = 0
const KEK_WRAP_P5_PAD = 1
const KEK_WRAP_0_PAD = 2
const KEK_WRAP_OAEP_PAD = 3
const KEK_WRAP_P1_PAD = 4
const KEK_WRAP_OAEP2_PAD = 5

const MODE_NULL = 0
const MODE_ECB = 1
const MODE_CBC = 2

// EMV CSR
const EFT_KEK_OP_RAW = 1
const EFT_KEK_OP_VISA1 = 2
const EFT_KEK_OP_LMK = 3
const EFT_KEK_OP_JCB = 4

export const state = {
  KEK_PAD_MAPPER: {
    [KEK_WRAP_NULL_PAD]: 'NO-PADDING',
    [KEK_WRAP_P5_PAD]: 'P5',
    [KEK_WRAP_0_PAD]: '0-PAD',
    [KEK_WRAP_OAEP_PAD]: 'OAEP',
    [KEK_WRAP_P1_PAD]: 'P1',
    [KEK_WRAP_OAEP2_PAD]: 'OAEP2'
  },
  EFT_KEK_OP_MAPPER: {
    [EFT_KEK_OP_RAW]: 'raw',
    [EFT_KEK_OP_VISA1]: 'visa1',
    [EFT_KEK_OP_LMK]: 'lmk',
    [EFT_KEK_OP_JCB]: 'jcb'
  },
  MODE_MAPPER: {
    [MODE_NULL]: 'NO-MODE',
    [MODE_ECB]: 'ECB',
    [MODE_CBC]: 'CBC'
  },
  keyComponents: {
    outBlock1: null,
    kcv1: null,
    outBlock2: null,
    kcv2: null,
    outBlock3: null,
    kcv3: null,
    kcvFinal: null
  },
  outBlock: null,
  rawOutBlock: null,
  // IMPORT PAGE
  translateOutBlock: null,
  rawTranslateOutBlock: null,
  createBlobProgress: null
}
export const getters = {
  getImportOptions: () => {
    return {
      symmetric: [
        {
          name: 'With KEK',
          value: 'With KEK',
          options: [
            {
              name: 'DES',
              value: 'DES',
              options: [
                {
                  name: '56 bits',
                  value: ALG_DES
                }
              ]
            },
            {
              name: '3DES',
              value: '3DES',
              options: [
                {
                  name: '112 bits',
                  value: ALG_3DES_112
                },
                {
                  name: '168 bits',
                  value: ALG_3DES_168
                }
              ]
            },
            {
              name: 'AES',
              value: 'AES',
              options: [
                {
                  name: '128 bits',
                  value: ALG_AES_128
                },
                {
                  name: '192 bits',
                  value: ALG_AES_192
                },
                {
                  name: '256 bits',
                  value: ALG_AES_256
                }
              ]
            }
          ]
        },
        {
          name: 'Key Components',
          value: 'Key Components',
          options: [
            {
              name: 'DES',
              value: 'DES',
              options: [
                {
                  name: '56 bits',
                  value: ALG_DES
                }
              ]
            },
            {
              name: '3DES',
              value: '3DES',
              options: [
                {
                  name: '112 bits',
                  value: ALG_3DES_112
                },
                {
                  name: '168 bits',
                  value: ALG_3DES_168
                }
              ]
            },
            {
              name: 'AES',
              value: 'AES',
              options: [
                {
                  name: '128 bits',
                  value: ALG_AES_128
                },
                {
                  name: '192 bits',
                  value: ALG_AES_192
                },
                {
                  name: '256 bits',
                  value: ALG_AES_256
                }
              ]
            }
          ]
        },
        {
          name: 'Clear Text Key',
          value: 'Clear Text Key',
          options: [
            {
              name: 'DES',
              value: 'DES',
              options: [
                {
                  name: '56 bits',
                  value: ALG_DES
                }
              ]
            },
            {
              name: '3DES',
              value: '3DES',
              options: [
                {
                  name: '112 bits',
                  value: ALG_3DES_112
                },
                {
                  name: '168 bits',
                  value: ALG_3DES_168
                }
              ]
            },
            {
              name: 'DESX',
              value: 'DESX',
              options: [
                {
                  name: '184 bits',
                  value: ALG_DESX
                }
              ]
            },
            {
              name: 'AES',
              value: 'AES',
              options: [
                {
                  name: '128 bits',
                  value: ALG_AES_128
                },
                {
                  name: '192 bits',
                  value: ALG_AES_192
                },
                {
                  name: '256 bits',
                  value: ALG_AES_256
                }
              ]
            },
            {
              name: 'ARC4',
              value: 'ARC4',
              options: [
                {
                  name: '128 bits',
                  value: ALG_ARC4
                }
              ]
            },
            {
              name: 'HMAC',
              value: 'HMAC',
              options: [
                {
                  name: 'MD5',
                  value: ALG_HMAC_MD5
                },
                {
                  name: 'SHA1',
                  value: ALG_HMAC_SHA1
                },
                {
                  name: 'SHA-256',
                  value: ALG_HMAC_SHA2_256
                },
                {
                  name: 'SHA-384',
                  value: ALG_HMAC_SHA2_384
                },
                {
                  name: 'SHA-512',
                  value: ALG_HMAC_SHA2_512
                }
              ]
            }
          ]
        }
      ],
      asymmetric: [
        {
          name: 'With KEK',
          value: 'With KEK',
          options: [
            {
              name: 'RSA',
              value: 'RSA',
              options: [
                {
                  name: '512 bits',
                  value: ALG_RSA_512
                },
                {
                  name: '1024 bits',
                  value: ALG_RSA_1024
                },
                {
                  name: '1152 bits',
                  value: ALG_RSA_1152
                },
                {
                  name: '1408 bits',
                  value: ALG_RSA_1408
                },
                {
                  name: '1984 bits',
                  value: ALG_RSA_1984
                },
                {
                  name: '2048 bits',
                  value: ALG_RSA_2048
                },
                {
                  name: '4096 bits',
                  value: ALG_RSA_4096
                },
                {
                  name: '8192 bits',
                  value: ALG_RSA_8192
                }
              ]
            },
            {
              name: 'ECC',
              value: 'ECC',
              options: [
                {
                  name: 'SecG Random',
                  value: 'SecG Random',
                  options: [
                    {
                      name: '112 bits',
                      value: '112 bits',
                      options: [
                        {
                          name: 'R1',
                          value: ALG_ECC_SECP112R1
                        },
                        {
                          name: 'R2',
                          value: ALG_ECC_SECP112R2
                        }
                      ]
                    },
                    {
                      name: '128 bits',
                      value: '128 bits',
                      options: [
                        {
                          name: 'R1',
                          value: ALG_ECC_SECP128R1
                        },
                        {
                          name: 'R2',
                          value: ALG_ECC_SECP128R2
                        }
                      ]
                    },
                    {
                      name: '160 bits',
                      value: '160 bits',
                      options: [
                        {
                          name: 'R1',
                          value: ALG_ECC_SECP160R1
                        },
                        {
                          name: 'R2',
                          value: ALG_ECC_SECP160R2
                        }
                      ]
                    },
                    {
                      name: '192 bits',
                      value: '192 bits',
                      options: [
                        {
                          name: 'R1',
                          value: ALG_ECC_SECP192R1
                        }
                      ]
                    },
                    {
                      name: '224 bits',
                      value: '224 bits',
                      options: [
                        {
                          name: 'R1',
                          value: ALG_ECC_SECP224R1
                        }
                      ]
                    },
                    {
                      name: '256 bits',
                      value: '256 bits',
                      options: [
                        {
                          name: 'R1',
                          value: ALG_ECC_SECP256R1
                        }
                      ]
                    },
                    {
                      name: '384 bits',
                      value: '384 bits',
                      options: [
                        {
                          name: 'R1',
                          value: ALG_ECC_SECP384R1
                        }
                      ]
                    },
                    {
                      name: '521 bits',
                      value: '521 bits',
                      options: [
                        {
                          name: 'R1',
                          value: ALG_ECC_SECP521R1
                        }
                      ]
                    }
                  ]
                },
                {
                  name: 'SecG Koblitz',
                  value: 'SecG Koblitz',
                  options: [
                    {
                      name: '160 bits',
                      value: '160 bits',
                      options: [
                        {
                          name: 'K1',
                          value: ALG_ECC_SECP160K1
                        }
                      ]
                    },
                    {
                      name: '192 bits',
                      value: '192 bits',
                      options: [
                        {
                          name: 'K1',
                          value: ALG_ECC_SECP192K1
                        }
                      ]
                    },
                    {
                      name: '224 bits',
                      value: '224 bits',
                      options: [
                        {
                          name: 'K1',
                          value: ALG_ECC_SECP224K1
                        }
                      ]
                    },
                    {
                      name: '256 bits',
                      value: '256 bits',
                      options: [
                        {
                          name: 'K1',
                          value: ALG_ECC_SECP256K1
                        }
                      ]
                    }
                  ]
                },
                {
                  name: 'NIST Random',
                  value: 'NIST Random',
                  options: [
                    {
                      name: '384 bits',
                      value: '384 bits',
                      options: [
                        {
                          name: 'R1',
                          value: ALG_ECC_SECP384R1
                        }
                      ]
                    },
                    {
                      name: '521 bits',
                      value: '521 bits',
                      options: [
                        {
                          name: 'R1',
                          value: ALG_ECC_SECP521R1
                        }
                      ]
                    }
                  ]
                },
                {
                  name: 'ISO X9.62',
                  value: 'ISO X9.62',
                  options: [
                    {
                      name: '192 bits',
                      value: '192 bits',
                      options: [
                        {
                          name: 'V1',
                          value: ALG_ECC_SECP192R1
                        },
                        {
                          name: 'V2',
                          value: ALG_ECC_X9_62_PRIME192V2
                        },
                        {
                          name: 'V3',
                          value: ALG_ECC_X9_62_PRIME192V3
                        }
                      ]
                    },
                    {
                      name: '239 bits',
                      value: '239 bits',
                      options: [
                        {
                          name: 'V1',
                          value: ALG_ECC_X9_62_PRIME239V1
                        },
                        {
                          name: 'V2',
                          value: ALG_ECC_X9_62_PRIME239V2
                        },
                        {
                          name: 'V3',
                          value: ALG_ECC_X9_62_PRIME239V3
                        }
                      ]
                    },
                    {
                      name: '256 bits',
                      value: '256 bits',
                      options: [
                        {
                          name: 'V1',
                          value: ALG_ECC_SECP256R1
                        }
                      ]
                    }
                  ]
                },
                {
                  name: 'Brainpool',
                  value: 'Brainpool',
                  options: [
                    {
                      name: '160 bits',
                      value: '160 bits',
                      options: [
                        {
                          name: 'Default',
                          value: ALG_ECC_BRAINPOOL_P160R1
                        },
                        {
                          name: 'Twisted',
                          value: ALG_ECC_BRAINPOOL_P160T1
                        }
                      ]
                    },
                    {
                      name: '192 bits',
                      value: '192 bits',
                      options: [
                        {
                          name: 'Default',
                          value: ALG_ECC_BRAINPOOL_P192R1
                        },
                        {
                          name: 'Twisted',
                          value: ALG_ECC_BRAINPOOL_P192T1
                        }
                      ]
                    },
                    {
                      name: '224 bits',
                      value: '224 bits',
                      options: [
                        {
                          name: 'Default',
                          value: ALG_ECC_BRAINPOOL_P224R1
                        },
                        {
                          name: 'Twisted',
                          value: ALG_ECC_BRAINPOOL_P224T1
                        }
                      ]
                    },
                    {
                      name: '256 bits',
                      value: '256 bits',
                      options: [
                        {
                          name: 'Default',
                          value: ALG_ECC_BRAINPOOL_P256R1
                        },
                        {
                          name: 'Twisted',
                          value: ALG_ECC_BRAINPOOL_P256T1
                        }
                      ]
                    },
                    {
                      name: '320 bits',
                      value: '320 bits',
                      options: [
                        {
                          name: 'Default',
                          value: ALG_ECC_BRAINPOOL_P320R1
                        },
                        {
                          name: 'Twisted',
                          value: ALG_ECC_BRAINPOOL_P320T1
                        }
                      ]
                    },
                    {
                      name: '384 bits',
                      value: '384 bits',
                      options: [
                        {
                          name: 'Default',
                          value: ALG_ECC_BRAINPOOL_P384R1
                        },
                        {
                          name: 'Twisted',
                          value: ALG_ECC_BRAINPOOL_P384T1
                        }
                      ]
                    },
                    {
                      name: '512 bits',
                      value: '512 bits',
                      options: [
                        {
                          name: 'Default',
                          value: ALG_ECC_BRAINPOOL_P512R1
                        },
                        {
                          name: 'Twisted',
                          value: ALG_ECC_BRAINPOOL_P512T1
                        }
                      ]
                    }
                  ]
                },
                {
                  name: 'Edwards',
                  value: 'Edwards',
                  options: [
                    {
                      name: '448 bits',
                      value: '448 bits',
                      options: [
                        {
                          name: 'Signature',
                          value: ALG_ECC_ED448
                        },
                        {
                          name: 'Key Exchange',
                          value: ALG_ECC_X448
                        }
                      ]
                    },
                    {
                      name: '256 bits',
                      value: '256 bits',
                      options: [
                        {
                          name: 'Signature',
                          value: ALG_ECC_ED25519
                        },
                        {
                          name: 'Key Exchange',
                          value: ALG_ECC_X25519
                        }
                      ]
                    }
                  ]
                }
              ]
            }
          ]
        },
        {
          name: 'PKCS#1',
          value: 'PKCS#1',
          options: [
            {
              name: 'RSA',
              value: 'RSA',
              options: [
                {
                  name: '512 bits',
                  value: ALG_RSA_512
                },
                {
                  name: '1024 bits',
                  value: ALG_RSA_1024
                },
                {
                  name: '1152 bits',
                  value: ALG_RSA_1152
                },
                {
                  name: '1408 bits',
                  value: ALG_RSA_1408
                },
                {
                  name: '1984 bits',
                  value: ALG_RSA_1984
                },
                {
                  name: '2048 bits',
                  value: ALG_RSA_2048
                },
                {
                  name: '4096 bits',
                  value: ALG_RSA_4096
                },
                {
                  name: '8192 bits',
                  value: ALG_RSA_8192
                }
              ]
            }
          ]
        },
        {
          name: 'PKCS#8',
          value: 'PKCS#8',
          options: [
            {
              name: 'RSA',
              value: 'RSA',
              options: [
                {
                  name: '512 bits',
                  value: ALG_RSA_512
                },
                {
                  name: '1024 bits',
                  value: ALG_RSA_1024
                },
                {
                  name: '1152 bits',
                  value: ALG_RSA_1152
                },
                {
                  name: '1408 bits',
                  value: ALG_RSA_1408
                },
                {
                  name: '1984 bits',
                  value: ALG_RSA_1984
                },
                {
                  name: '2048 bits',
                  value: ALG_RSA_2048
                },
                {
                  name: '4096 bits',
                  value: ALG_RSA_4096
                },
                {
                  name: '8192 bits',
                  value: ALG_RSA_8192
                }
              ]
            },
            {
              name: 'ECC',
              value: 'ECC',
              options: [
                {
                  name: 'SecG Random',
                  value: 'SecG Random',
                  options: [
                    {
                      name: '112 bits',
                      value: '112 bits',
                      options: [
                        {
                          name: 'R1',
                          value: ALG_ECC_SECP112R1
                        },
                        {
                          name: 'R2',
                          value: ALG_ECC_SECP112R2
                        }
                      ]
                    },
                    {
                      name: '128 bits',
                      value: '128 bits',
                      options: [
                        {
                          name: 'R1',
                          value: ALG_ECC_SECP128R1
                        },
                        {
                          name: 'R2',
                          value: ALG_ECC_SECP128R2
                        }
                      ]
                    },
                    {
                      name: '160 bits',
                      value: '160 bits',
                      options: [
                        {
                          name: 'R1',
                          value: ALG_ECC_SECP160R1
                        },
                        {
                          name: 'R2',
                          value: ALG_ECC_SECP160R2
                        }
                      ]
                    },
                    {
                      name: '192 bits',
                      value: '192 bits',
                      options: [
                        {
                          name: 'R1',
                          value: ALG_ECC_SECP192R1
                        }
                      ]
                    },
                    {
                      name: '224 bits',
                      value: '224 bits',
                      options: [
                        {
                          name: 'R1',
                          value: ALG_ECC_SECP224R1
                        }
                      ]
                    },
                    {
                      name: '256 bits',
                      value: '256 bits',
                      options: [
                        {
                          name: 'R1',
                          value: ALG_ECC_SECP256R1
                        }
                      ]
                    },
                    {
                      name: '384 bits',
                      value: '384 bits',
                      options: [
                        {
                          name: 'R1',
                          value: ALG_ECC_SECP384R1
                        }
                      ]
                    },
                    {
                      name: '521 bits',
                      value: '521 bits',
                      options: [
                        {
                          name: 'R1',
                          value: ALG_ECC_SECP521R1
                        }
                      ]
                    }
                  ]
                },
                {
                  name: 'SecG Koblitz',
                  value: 'SecG Koblitz',
                  options: [
                    {
                      name: '160 bits',
                      value: '160 bits',
                      options: [
                        {
                          name: 'K1',
                          value: ALG_ECC_SECP160K1
                        }
                      ]
                    },
                    {
                      name: '192 bits',
                      value: '192 bits',
                      options: [
                        {
                          name: 'K1',
                          value: ALG_ECC_SECP192K1
                        }
                      ]
                    },
                    {
                      name: '224 bits',
                      value: '224 bits',
                      options: [
                        {
                          name: 'K1',
                          value: ALG_ECC_SECP224K1
                        }
                      ]
                    },
                    {
                      name: '256 bits',
                      value: '256 bits',
                      options: [
                        {
                          name: 'K1',
                          value: ALG_ECC_SECP256K1
                        }
                      ]
                    }
                  ]
                },
                {
                  name: 'NIST Random',
                  value: 'NIST Random',
                  options: [
                    {
                      name: '384 bits',
                      value: '384 bits',
                      options: [
                        {
                          name: 'R1',
                          value: ALG_ECC_SECP384R1
                        }
                      ]
                    },
                    {
                      name: '521 bits',
                      value: '521 bits',
                      options: [
                        {
                          name: 'R1',
                          value: ALG_ECC_SECP521R1
                        }
                      ]
                    }
                  ]
                },
                {
                  name: 'ISO X9.62',
                  value: 'ISO X9.62',
                  options: [
                    {
                      name: '192 bits',
                      value: '192 bits',
                      options: [
                        {
                          name: 'V1',
                          value: ALG_ECC_SECP192R1
                        },
                        {
                          name: 'V2',
                          value: ALG_ECC_X9_62_PRIME192V2
                        },
                        {
                          name: 'V3',
                          value: ALG_ECC_X9_62_PRIME192V3
                        }
                      ]
                    },
                    {
                      name: '239 bits',
                      value: '239 bits',
                      options: [
                        {
                          name: 'V1',
                          value: ALG_ECC_X9_62_PRIME239V1
                        },
                        {
                          name: 'V2',
                          value: ALG_ECC_X9_62_PRIME239V2
                        },
                        {
                          name: 'V3',
                          value: ALG_ECC_X9_62_PRIME239V3
                        }
                      ]
                    },
                    {
                      name: '256 bits',
                      value: '256 bits',
                      options: [
                        {
                          name: 'V1',
                          value: ALG_ECC_SECP256R1
                        }
                      ]
                    }
                  ]
                },
                {
                  name: 'Brainpool',
                  value: 'Brainpool',
                  options: [
                    {
                      name: '160 bits',
                      value: '160 bits',
                      options: [
                        {
                          name: 'Default',
                          value: ALG_ECC_BRAINPOOL_P160R1
                        },
                        {
                          name: 'Twisted',
                          value: ALG_ECC_BRAINPOOL_P160T1
                        }
                      ]
                    },
                    {
                      name: '192 bits',
                      value: '192 bits',
                      options: [
                        {
                          name: 'Default',
                          value: ALG_ECC_BRAINPOOL_P192R1
                        },
                        {
                          name: 'Twisted',
                          value: ALG_ECC_BRAINPOOL_P192T1
                        }
                      ]
                    },
                    {
                      name: '224 bits',
                      value: '224 bits',
                      options: [
                        {
                          name: 'Default',
                          value: ALG_ECC_BRAINPOOL_P224R1
                        },
                        {
                          name: 'Twisted',
                          value: ALG_ECC_BRAINPOOL_P224T1
                        }
                      ]
                    },
                    {
                      name: '256 bits',
                      value: '256 bits',
                      options: [
                        {
                          name: 'Default',
                          value: ALG_ECC_BRAINPOOL_P256R1
                        },
                        {
                          name: 'Twisted',
                          value: ALG_ECC_BRAINPOOL_P256T1
                        }
                      ]
                    },
                    {
                      name: '320 bits',
                      value: '320 bits',
                      options: [
                        {
                          name: 'Default',
                          value: ALG_ECC_BRAINPOOL_P320R1
                        },
                        {
                          name: 'Twisted',
                          value: ALG_ECC_BRAINPOOL_P320T1
                        }
                      ]
                    },
                    {
                      name: '384 bits',
                      value: '384 bits',
                      options: [
                        {
                          name: 'Default',
                          value: ALG_ECC_BRAINPOOL_P384R1
                        },
                        {
                          name: 'Twisted',
                          value: ALG_ECC_BRAINPOOL_P384T1
                        }
                      ]
                    },
                    {
                      name: '512 bits',
                      value: '512 bits',
                      options: [
                        {
                          name: 'Default',
                          value: ALG_ECC_BRAINPOOL_P512R1
                        },
                        {
                          name: 'Twisted',
                          value: ALG_ECC_BRAINPOOL_P512T1
                        }
                      ]
                    }
                  ]
                },
                {
                  name: 'Edwards',
                  value: 'Edwards',
                  options: [
                    {
                      name: '448 bits',
                      value: '448 bits',
                      options: [
                        {
                          name: 'Signature',
                          value: ALG_ECC_ED448
                        },
                        {
                          name: 'Key Exchange',
                          value: ALG_ECC_X448
                        }
                      ]
                    },
                    {
                      name: '256 bits',
                      value: '256 bits',
                      options: [
                        {
                          name: 'Signature',
                          value: ALG_ECC_ED25519
                        },
                        {
                          name: 'Key Exchange',
                          value: ALG_ECC_X25519
                        }
                      ]
                    }
                  ]
                }
              ]
            }
          ]
        },
        {
          name: 'PKCS#12',
          value: 'PKCS#12'
        }
      ],
      others: [
        {
          name: 'File',
          value: 'File'
        },
        {
          name: 'Certificate',
          value: 'Certificate'
        },
        {
          name: 'PKCS#7',
          value: 'PKCS#7'
        },
        {
          name: 'PSKC Translate',
          value: 'PSKC Translate'
        }
      ]
    }
  },
  getHexDump: state => ({ buffer }) => {
    const length = buffer.byteLength
    const my = new Uint8Array(buffer)
    let hex = ''
    for (let i = 0; i < length; i++) {
      let byteHexString = my[i].toString(16)
      if (byteHexString.length < 2) byteHexString = '0' + byteHexString
      hex += byteHexString
    }
    return hex.toUpperCase()
  },
  getPaddingState: state => ({ padding }) => {
    if (!Object.prototype.hasOwnProperty.call(state.KEK_PAD_MAPPER, padding)) {
      return padding
    }
    return state.KEK_PAD_MAPPER[padding]
  },
  getHashState: state => (hash) => {
    if (!Object.prototype.hasOwnProperty.call(state.CSR_HASH_MAPPER, hash.hash)) {
      return hash
    }
    const val = state.CSR_HASH_MAPPER[hash]
    return val
  },
  getModeState: state => ({ mode }) => {
    if (!Object.prototype.hasOwnProperty.call(state.MODE_MAPPER, mode)) {
      return mode
    }
    return state.MODE_MAPPER[mode]
  },
  getCSRTypeState: state => ({ csrType }) => {
    if (!Object.prototype.hasOwnProperty.call(state.EFT_KEK_OP_MAPPER, csrType)) {
      return csrType
    }
    return state.EFT_KEK_OP_MAPPER[csrType]
  }
}
export const mutations = {
  SET_outBlock (state, payload = null) {
    state.outBlock = payload
  },
  SET_kcv (state, payload = null) {
    state.kcv = payload
  },
  SET_outBlock1 (state, payload = null) {
    state.keyComponents.outBlock1 = payload
  },
  SET_kcv1 (state, payload = null) {
    state.keyComponents.kcv1 = payload
  },
  SET_outBlock2 (state, payload = null) {
    state.keyComponents.outBlock2 = payload
  },
  SET_kcv2 (state, payload = null) {
    state.keyComponents.kcv2 = payload
  },
  SET_outBlock3 (state, payload = null) {
    state.keyComponents.outBlock3 = payload
  },
  SET_kcv3 (state, payload = null) {
    state.keyComponents.kcv3 = payload
  },
  SET_kcvFinal (state, payload = null) {
    state.keyComponents.kcvFinal = payload
  },
  SET_rawOutBlock (state, payload = null) {
    state.rawOutBlock = payload
  },
  // IMPORT PAGE
  SET_translateOutBlock (state, payload = null) {
    state.translateOutBlock = payload
  },
  SET_rawTranslateOutBlock (state, payload = null) {
    state.rawTranslateOutBlock = payload
  },
  SET_createBlobProgress (state, payload = null) {
    state.createBlobProgress = payload
  },
  RESET (state) {
    state.outBlock = null
    state.rawOutBlock = null
    state.keyComponents.kcvFinal = null
    state.keyComponents.kcv = null
    state.keyComponents.outBlock1 = null
    state.keyComponents.kcv1 = null
    state.keyComponents.outBlock2 = null
    state.keyComponents.kcv2 = null
    state.keyComponents.outBlock3 = null
    state.keyComponents.kcv3 = null
    state.keyComponents.kcvFinal = null
    state.keyComponents.kcvFinal = null
    state.keyComponents.kcvFinal = null
  }
}
export const actions = {
  async exportObj ({ rootState, dispatch, commit }, { obj }) {
    const hsm = rootState.hsm.instance
    const body = {
      obj
    }
    try {
      const res = await hsm.exportObject(body)
      commit('SET_rawOutBlock', res)
      return res
    } catch (err) {
      dispatch('error/setError', { error: err }, { root: true })
      throw err
    }
  },
  async exportPubKey ({ rootState, dispatch, commit }, { obj }) {
    const hsm = rootState.hsm.instance
    const body = {
      obj
    }
    try {
      const res = await hsm.exportObject(body)
      commit('SET_rawOutBlock', res)
      return res
    } catch (err) {
      dispatch('error/setError', { error: err }, { root: true })
      throw err
    }
  },
  async exportP1 ({ rootState, dispatch, commit }, { obj }) {
    const hsm = rootState.hsm.instance
    const body = {
      obj
    }
    try {
      const res = await hsm.exportObject(body)
      const pbkSchema = new asn1js.Sequence({
        name: 'block1',
        value: [
          new asn1js.Integer(),
          new asn1js.Integer()
        ]
      })
      const variant = asn1js.verifySchema(res, pbkSchema)

      if (variant.result) {
        const pbkEndIndex = variant.result.blockLength
        const pkLength = res.byteLength - pbkEndIndex
        const onlyPk = new Uint8Array(pkLength)
        const pbkAndPk = new Uint8Array(res)
        for (let i = pbkEndIndex; i < res.byteLength; i++) {
          onlyPk[i - pbkEndIndex] = pbkAndPk[i]
        }
        commit('SET_rawOutBlock', onlyPk.buffer)
        return onlyPk.buffer
      } else {
        commit('SET_rawOutBlock', res)
        return res
      }
    } catch (err) {
      dispatch('error/setError', { error: err }, { root: true })
      throw err
    }
  },
  async exportCert ({ rootState, dispatch, commit }, { obj, isPrivateKey }) {
    const hsm = rootState.hsm.instance
    const body = {
      obj
    }
    try {
      const res = await hsm.exportObject(body)
      commit('SET_rawOutBlock', res)
      return res
    } catch (err) {
      dispatch('error/setError', { error: err }, { root: true })
      throw err
    }
  },
  async expAsymPub ({ rootState, dispatch, commit }, { obj, x509 }) {
    const hsm = rootState.hsm.instance
    const body = {
      obj,
      x509
    }
    try {
      const res = await hsm.getAsymPubExp({ body })
      const resInBytes = hexStringToBuffer(res.pbk)
      commit('SET_rawOutBlock', resInBytes)
      // const asn1 = asn1js.fromBER(resInBytes)
      // const pubkey = new RSAPublicKey({ schema: asn1.result })
      // commit('SET_rawOutBlock', pubkey)

      return res.buffer
    } catch (err) {
      dispatch('error/setError', { error: err }, { root: true })
      throw err
    }
  },

  async expKeyComponents ({ rootState, dispatch, commit }, { key, size, jcb }) {
    const hsm = rootState.hsm.instance
    const body = {
      key
    }

    try {
      const res = await hsm.expKeyComponents({ body })
      if (jcb) {
        commit('SET_kcv1', res.cks1.substring(0, 6))
        commit('SET_kcv2', res.cks2.substring(0, 6))
        commit('SET_kcv3', res.cks3.substring(0, 6))
        commit('SET_kcvFinal', res.cks.substring(0, 6))
      } else {
        commit('SET_kcv1', res.cks1.substring(0, 6))
        commit('SET_kcv2', res.cks2.substring(0, 6))
        commit('SET_kcv3', res.cks3.substring(0, 6))
        commit('SET_kcvFinal', res.cks.substring(0, 6))
      }
      commit('SET_outBlock1', res.part1.replace(/0+$/, ''))
      commit('SET_outBlock2', res.part2.replace(/0+$/, ''))
      commit('SET_outBlock3', res.part3.replace(/0+$/, ''))
      return res
    } catch (err) {
      dispatch('error/setError', { error: err }, { root: true })
      throw err
    }
  },
  async kekWrap ({ state, rootState, dispatch, commit }, body) {
    const hsm = rootState.hsm.instance

    try {
      const res = await hsm.kekWrap(body)
      commit('SET_rawOutBlock', res)
      return res
    } catch (err) {
      dispatch('error/setError', { error: err }, { root: true })
      throw err
    }
  },

  async expP8 ({ state, rootState, dispatch, commit }, { obj, pwd }) {
    const hsm = rootState.hsm.instance
    const body = {
      obj,
      pwd: pwd || ''
    }

    try {
      const res = await hsm.exportP8(body)
      commit('SET_rawOutBlock', res)
      return res
    } catch (err) {
      dispatch('error/setError', { error: err }, { root: true })
      throw err
    }
  },

  async expP12 ({ state, rootState, dispatch, commit }, { key, cert, pwd }) {
    const hsm = rootState.hsm.instance
    const body = {
      key,
      cert,
      pwd: pwd || ''
    }

    try {
      const res = await hsm.exportP12(body)
      commit('SET_rawOutBlock', res)
      return res
    } catch (err) {
      dispatch('error/setError', { error: err }, { root: true })
      throw err
    }
  },

  async p10CSR ({ state, rootState, dispatch, commit }, { obj, hash, dn }) {
    const hsm = rootState.hsm.instance
    const body = {
      obj,
      hash,
      dn
    }

    try {
      const res = await hsm.p10CSR(body)
      // const pemWithoutHeader = pemToSingleLineBase64(res)
      // commit('SET_rawOutBlock', stringToArrayBuffer(fromBase64(pemWithoutHeader)))
      commit('SET_rawOutBlock', res)
      return res
    } catch (err) {
      dispatch('error/setError', { error: err }, { root: true })
      throw err
    }
  },
  // IMPORT
  async importKey ({ rootState, dispatch }, { type, attr, obj, mat }) {
    const hsm = rootState.hsm.instance
    const body = {
      obj,
      type,
      attr,
      mat
    }
    try {
      await hsm.importKey({ body })
    } catch (err) {
      dispatch('error/setError', { error: err }, { root: true })
      throw err
    }
  },
  async impKeyComponents ({ rootState, dispatch }, { type, key, part1, cks1, part2, cks2, part3, cks3, cks }) {
    const hsm = rootState.hsm.instance
    const body = {
      type,
      key,
      part1,
      cks1,
      part2,
      cks2,
      part3,
      cks3,
      cks
    }
    try {
      const res = await hsm.impKeyComponents({ body })
      return res
    } catch (err) {
      dispatch('error/setError', { error: err }, { root: true })
      throw err
    }
  },
  async impAesComponents ({ rootState, dispatch }, { type, ckst, attr, key, part1, part2, part3, cks }) {
    const hsm = rootState.hsm.instance
    const body = {
      type,
      ckst,
      attr,
      key,
      part1,
      part2,
      part3,
      cks
    }
    try {
      const res = await hsm.impAesComponents({ body })
      return res
    } catch (err) {
      dispatch('error/setError', { error: err }, { root: true })
      throw err
    }
  },
  async expAesComponents ({ rootState, dispatch }, { ckst, key }) {
    const hsm = rootState.hsm.instance
    const body = {
      ckst,
      key
    }
    try {
      const res = await hsm.expAesComponents({ body })
      return res
    } catch (err) {
      dispatch('error/setError', { error: err }, { root: true })
      throw err
    }
  },
  async importP8 (
    { rootState, dispatch },
    { obj, pwd, type, attr, pem }
  ) {
    const hsm = rootState.hsm.instance
    const body = {
      obj,
      pwd: pwd || '',
      type,
      attr,
      pem
    }
    try {
      await hsm.importP8({ body })
    } catch (err) {
      dispatch('error/setError', { error: err }, { root: true })
      throw err
    }
  },
  async importP12 (
    { rootState, dispatch },
    { key, pbk, cert, pwd, attr, pfx }
  ) {
    const hsm = rootState.hsm.instance
    const body = {
      key,
      pbk: pbk || '',
      cert,
      pwd: pwd || '',
      attr,
      pfx
    }
    try {
      await hsm.importP12({ body })
    } catch (err) {
      dispatch('error/setError', { error: err }, { root: true })
      throw err
    }
  },
  async pskcTranslate ({ rootState, dispatch, commit }, { key, psk, pskc }) {
    const hsm = rootState.hsm.instance
    const body = {
      key,
      psk,
      pskc
    }
    try {
      const res = await hsm.pskcTranslate({ body })
      const prettyJSON = JSON.stringify(res.oath_blob, null, 2)
      commit('SET_translateOutBlock', prettyJSON)
      return res
    } catch (err) {
      dispatch('error/setError', { error: err }, { root: true })
      throw err
    }
  },
  async createBlob ({ rootState, dispatch, commit }, { name, file }) {
    const hsm = rootState.hsm.instance
    const req = hsm.createBlob({ name, file })
    return new Promise((resolve, reject) => {
      req
        .on('progress', () => {
          commit('SET_createBlobProgress', event.percent)
        })
        .then(() => {
          resolve()
        })
        .catch(err => {
          const errBody = getSafe(() => err.response.body, err.message)
          dispatch('error/setError', { error: errBody }, { root: true })
          reject(errBody)
        })
        .finally(() => {
          commit('SET_createBlobProgress', null)
        })
    })
  }
}
